import React, { useState } from 'react';
import type { ConversationSummary } from '../types';
import './ConversationSidebar.css';

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

export interface ConversationSidebarProps {
  conversations: ConversationSummary[];
  activeId: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onRename: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  activeId,
  loading,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const startEdit = (c: ConversationSummary) => {
    setEditingId(c.id);
    setEditTitle(c.title);
  };

  const submitRename = async () => {
    if (!editingId || !editTitle.trim()) {
      setEditingId(null);
      return;
    }
    await onRename(editingId, editTitle.trim());
    setEditingId(null);
    setEditTitle('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const confirmDelete = async (id: string) => {
    if (deletingId !== id) {
      setDeletingId(id);
      return;
    }
    await onDelete(id);
    setDeletingId(null);
  };

  const cancelDelete = () => {
    setDeletingId(null);
  };

  return (
    <aside className="conversation-sidebar">
      <button
        type="button"
        className="conversation-sidebar-new neuro-silkafont-regular"
        onClick={onNewChat}
      >
        + New Chat
      </button>

      <div className="conversation-sidebar-list">
        {loading ? (
          <>
            {[1, 2, 3].map((i) => (
              <div key={i} className="conversation-sidebar-skeleton" aria-hidden="true">
                <div className="conversation-sidebar-skeleton-line" />
                <div className="conversation-sidebar-skeleton-line short" />
              </div>
            ))}
          </>
        ) : (
          conversations.map((c) => (
            <div
              key={c.id}
              className={`conversation-sidebar-item ${activeId === c.id ? 'active' : ''}`}
            >
              {editingId === c.id ? (
                <div className="conversation-sidebar-edit">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitRename();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    className="conversation-sidebar-edit-input neuro-silkafont-regular"
                    autoFocus
                  />
                  <div className="conversation-sidebar-edit-actions">
                    <button type="button" onClick={submitRename} className="conversation-sidebar-edit-btn">
                      Save
                    </button>
                    <button type="button" onClick={cancelEdit} className="conversation-sidebar-edit-btn cancel">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : deletingId === c.id ? (
                <div className="conversation-sidebar-delete-confirm">
                  <span className="conversation-sidebar-delete-text">Delete?</span>
                  <div className="conversation-sidebar-delete-actions">
                    <button type="button" onClick={() => confirmDelete(c.id)} className="conversation-sidebar-delete-yes">
                      Yes
                    </button>
                    <button type="button" onClick={cancelDelete} className="conversation-sidebar-edit-btn cancel">
                      No
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="conversation-sidebar-item-btn neuro-silkafont-regular"
                    onClick={() => onSelect(c.id)}
                  >
                    <span className="conversation-sidebar-item-title">{c.title}</span>
                    <span className="conversation-sidebar-item-date">{formatRelativeDate(c.updatedAt)}</span>
                  </button>
                  <div className="conversation-sidebar-item-actions">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); startEdit(c); }}
                      className="conversation-sidebar-action"
                      aria-label="Rename"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); confirmDelete(c.id); }}
                      className="conversation-sidebar-action"
                      aria-label="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </aside>
  );
};
